import { Injectable, Injector } from '@angular/core';
import {
  HttpDataService,
  ClientsService,
  DatetimeQuestion,
  Distributor,
  DistributorsService,
  QuestionBase,
  EntityQuestion,
  NumberQuestion,
  TextboxQuestion,
  ProfileService
} from '../core';
import { ROLES } from 'core/services/assets/constants';
import { DateTime } from 'luxon';
import { InfoExpirationDateQuestion } from 'core/components/forms/info-expiration-date-question';

const CREDITS_SALE = 'creditsSale';
const CREDITS_PURCHASE = 'creditsPurchase';
const SUBSCRIPTIONS_SALE = 'subscriptionsSale';
const SUBSCRIPTIONS_PURCHASE = 'subscriptionsPurchase';

@Injectable()
export class BrokerCreditsDealerService {
  public CREDITS_SALE = CREDITS_SALE;
  public CREDITS_PURCHASE = CREDITS_PURCHASE;
  public SUBSCRIPTIONS_SALE = SUBSCRIPTIONS_SALE;
  public SUBSCRIPTIONS_PURCHASE = SUBSCRIPTIONS_PURCHASE;
  public brokerBalanceQuestions: QuestionBase<any>[];
  public userRole;

  constructor(
    protected injector: Injector,
    public distributorsService: DistributorsService,
    public clientsService: ClientsService,
    public profileService: ProfileService
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => (this.userRole = userRole));
  }

  getPurchasesDataService(distributor: Distributor): HttpDataService {
    if (distributor.subscriptions.credits) {
      return new HttpDataService(this.injector, `/core/distributors/${distributor.id}/credits/purchases`);
    } else {
      return new HttpDataService(this.injector, `/core/distributors/${distributor.id}/users/purchases`);
    }
  }

  getSalesDataService(distributor: Distributor): HttpDataService {
    if (distributor.subscriptions.credits) {
      return new HttpDataService(this.injector, `/core/distributors/${distributor.id}/credits/sales`);
    } else {
      return new HttpDataService(this.injector, `/core/distributors/${distributor.id}/users/sales`);
    }
  }

  getConsumanbleSubscription(clientId: number): HttpDataService {
    return new HttpDataService(this.injector, `/subscriptions/${clientId}/consumable-subcriptions`);
  }

  createTransaction(transactionType, distributor: Distributor, data) {
    const transactions = {};
    let balancePurchase = SUBSCRIPTIONS_PURCHASE;
    let balanceSale = SUBSCRIPTIONS_SALE;
    if (distributor.subscriptions.credits) {
      balancePurchase = CREDITS_PURCHASE;
      balanceSale = CREDITS_SALE;
    }
    transactions[balancePurchase] = () => this.distributorsService.purchaseBalance(distributor, data);
    transactions[balanceSale] = () => this.distributorsService.sellBalance(distributor, data);
    return transactions[transactionType]();
  }

  getQuestions(
    transactionType: string,
    distributor: Distributor,
    client: any,
    isDecrease?: boolean
  ): QuestionBase<any>[] {
    const transactionQuestions = {};
    let balancePurchase = SUBSCRIPTIONS_PURCHASE;
    let balanceSale = SUBSCRIPTIONS_SALE;
    if (distributor.subscriptions.credits) {
      balancePurchase = CREDITS_PURCHASE;
      balanceSale = CREDITS_SALE;
    }
    transactionQuestions[balancePurchase] = () => this.getPurchaseQuestions(distributor, isDecrease);
    transactionQuestions[balanceSale] = () => this.getSaleQuestions(distributor, client);
    return transactionQuestions[transactionType]();
  }

  public getBaseQuestions(
    distributor: Distributor,
    minDate?: Date,
    clientRequired?: boolean,
    client?: any,
    isDecrease?: boolean
  ): QuestionBase<any>[] {
    const startDate = DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

    let expirationDate;
    if (isDecrease) {
      expirationDate = DateTime.fromISO(String(distributor.brokerBalance.expirationDate));
    } else {
      expirationDate = DateTime.local().plus({ year: 1 }).set({ hour: 23, minute: 59, second: 59, millisecond: 999 });
      if (minDate && expirationDate < DateTime.fromJSDate(minDate)) {
        expirationDate = DateTime.fromISO(String(distributor.brokerBalance.expirationDate)).plus({ days: 1 });
      }
    }

    this.brokerBalanceQuestions = [
      new NumberQuestion({
        key: 'amount',
        label:
          (distributor.subscriptions.users ? 'credits.order.seatsAmount' : 'credits.order.credits') +
          (isDecrease ? 'ToDecrease' : ''),
        class: 'col-2-offset',
        validators: {
          required: true,
          minValue: { limit: 0 },
          maxValue:
            clientRequired || isDecrease
              ? {
                  limit: distributor.brokerBalance.amount
                }
              : null
        },
        value: isDecrease ? 0 : null,
        order: 1
      }),
      new TextboxQuestion({
        key: 'reference',
        label: 'nouns.reference',
        class: 'col-1',
        order: 4
      }),
      new TextboxQuestion({
        key: 'comments',
        label: 'nouns.comments',
        class: 'col-1',
        order: 5
      })
    ];
    if (!isDecrease && (distributor.subscriptions.credits || clientRequired)) {
      this.brokerBalanceQuestions.push(
        new EntityQuestion({
          key: 'clientId',
          label: 'nouns.client',
          value: client ? client.id : null,
          entity: client,
          entityOptions: {
            valueField: 'id',
            filters: { distributorId: distributor.id }
          },
          eventHandlers: {
            onChange: (clientId) => {
              if (distributor.subscriptions.users) {
                if (clientId) {
                  this.updateInfoExpirationDate(clientId);
                } else {
                  this.getDatesQuestions(distributor, startDate, minDate, expirationDate, clientRequired);
                }
              }
            }
          },
          editable: !client,
          validators: { required: clientRequired },
          dataProvider: this.clientsService,
          order: clientRequired ? 0 : 2
        })
      );
    }
    if (distributor.type !== 'expert') {
      this.getDatesQuestions(distributor, startDate, minDate, expirationDate, clientRequired);
    }
    this.reorderQuestions();
    return this.brokerBalanceQuestions;
  }

  getPurchaseQuestions(distributor: Distributor, isDecrease?: boolean): QuestionBase<any>[] {
    const minDate = isDecrease ? new Date() : new Date(distributor.brokerBalance.expirationDate);
    return this.getBaseQuestions(distributor, minDate, null, null, isDecrease);
  }

  getSaleQuestions(distributor: Distributor, client) {
    return this.getBaseQuestions(distributor, null, true, client);
  }

  getDatesQuestions(distributor, startDate, minDate, expirationDate, isAssignSale: boolean) {
    const infoIndex = this.brokerBalanceQuestions.findIndex(
      (question) => question && question.key === 'expirationDateInfo'
    );
    if (infoIndex > -1) {
      this.brokerBalanceQuestions.splice(infoIndex, 1);
    }

    const dateIndex = this.brokerBalanceQuestions.findIndex(
      (question) => question && question.key === 'expirationDate'
    );
    if (dateIndex === -1) {
      if (distributor.subscriptions.users && isAssignSale) {
        this.brokerBalanceQuestions.push(
          new DatetimeQuestion({
            key: 'startDate',
            label: 'nouns.startDate',
            class: 'col-2',
            timezone: distributor.timezone,
            defaultHour: 0,
            defaultMinute: 0,
            defaultSeconds: 0,
            editable: true,
            value: startDate.toJSDate(),
            validators: { required: true, invalidDate: true },
            order: 2
          })
        );
      }
      this.brokerBalanceQuestions.push(
        new DatetimeQuestion({
          key: 'expirationDate',
          label: 'nouns.expirationDate',
          class: distributor.subscriptions.users ? 'col-2' : 'col-1',
          timezone: distributor.timezone,
          minDate,
          defaultHour: 23,
          defaultMinute: 59,
          defaultSeconds: 59,
          editable:
            distributor.subscriptions.credits ||
            [ROLES.SUPER_ADMIN, ROLES.DISTRIBUTOR_MANAGER, ROLES.SUPPORT_SPECIALIST, ROLES.CSADMIN, ROLES.CSM].includes(
              this.userRole.name
            ),
          value: expirationDate.toJSDate(),
          validators: { required: true, invalidDate: true },
          order: 3
        })
      );
      this.reorderQuestions();
    }
  }

  getInfoExpirationDateQuestion() {
    const dateIndex = this.brokerBalanceQuestions.findIndex((question) => question && question.key === 'startDate');
    if (dateIndex > -1) {
      this.brokerBalanceQuestions.splice(dateIndex, 2);
    }
    const infoIndex = this.brokerBalanceQuestions.findIndex(
      (question) => question && question.key === 'expirationDateInfo'
    );
    if (infoIndex === -1) {
      this.brokerBalanceQuestions.push(
        new InfoExpirationDateQuestion({
          key: 'expirationDateInfo',
          class: 'col-1',
          value: {
            seatsAmount: null,
            expirationDate: null
          },
          order: 2
        })
      );
      this.reorderQuestions();
    }
  }

  updateInfoExpirationDate(clientId: number) {
    this.getConsumanbleSubscription(clientId)
      .fetch()
      .then((clientBrokerBalance) => {
        if (clientBrokerBalance && clientBrokerBalance.length > 0) {
          this.getInfoExpirationDateQuestion();
          this.setInfoExpirationDateValues(clientBrokerBalance);
        }
      });
  }

  setInfoExpirationDateValues(clientBalance: any[]) {
    const expirationDateInfoIndex = this.brokerBalanceQuestions.findIndex(
      (question) => question && question.key === 'expirationDateInfo'
    );

    this.brokerBalanceQuestions[expirationDateInfoIndex].value['seatsAmount'] = clientBalance[0].rateAvailable;
    this.brokerBalanceQuestions[expirationDateInfoIndex].value['expirationDate'] = new Date(
      clientBalance[0].endDate
    ).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  reorderQuestions() {
    return this.brokerBalanceQuestions.sort((a, b) => a && b && a.order - b.order).filter((question) => !!question);
  }
}
